import React from "react";
import "./reviewHistory.scss";
import { ReviewEntry } from "src/store/modelLifeCycleGroundTruthSlice";
import { HIERARCHY_SEPARATOR } from "src/constants";
import { useAppSelector } from "src/store/store";
import { isAdminUser } from "src/utils/permissionUtil";
import { COLORS } from "../constants";
import { ColorInfo } from "../types";
import { filterReviewHistoryByAdminTimestamp } from "../helpers";

interface ReviewHistoryProps {
  reviewHistory: ReviewEntry[];
  fieldName: string;
  metadata?: Record<string, any>;
  showAdditionalInputs?: boolean;
}

const ReviewHistory: React.FC<ReviewHistoryProps> = ({
  reviewHistory,
  fieldName,
  metadata,
  showAdditionalInputs,
}) => {
  const createAnswerColorMap = (
    reviewHistory: ReviewEntry[],
    fieldName: string,
  ): Record<string, ColorInfo> => {
    const uniqueAnswers = new Set<string>();

    // Collect all answers from the review history
    reviewHistory.forEach((entry) => {
      entry.fields
        .filter((field) => field.fieldName === fieldName)[0]
        .answer.forEach((answerItem: string) => {
          uniqueAnswers.add(answerItem);
        });
    });

    const answerColorMap: Record<string, ColorInfo> = {};

    Array.from(uniqueAnswers).forEach((answer, index) => {
      // Use modulo to cycle through colors if there are more answers than colors
      const colorIndex = index % COLORS.length;
      answerColorMap[answer] = COLORS[colorIndex];
    });

    return answerColorMap;
  };

  const deduplicateReviewsByReviewer = (reviewHistory: ReviewEntry[]): ReviewEntry[] => {
    return Object.values(
      Array.from(reviewHistory).reduce<Record<string, ReviewEntry>>((acc, review) => {
        const reviewer = review.reviewer;
        if (
          !acc[reviewer] ||
          new Date(review.reviewSubmittedAt) > new Date(acc[reviewer].reviewSubmittedAt)
        ) {
          acc[reviewer] = review;
        }

        return acc;
      }, {}),
    );
  };

  // Get user information
  const { ldap, alias } = useAppSelector((state) => state.user);

  // Filter review history based on admin assignment timestamp
  let filteredReviewHistory = filterReviewHistoryByAdminTimestamp(reviewHistory, metadata, alias);

  // Deduplicate reviews by reviewer
  filteredReviewHistory = deduplicateReviewsByReviewer(filteredReviewHistory);

  // Create the color map using the passed index
  const answerColorMap = createAnswerColorMap(filteredReviewHistory, fieldName);

  return (
    <div className="review-history-container">
      {filteredReviewHistory
        .sort((a, b) => {
          // TODO: hard code logic for special case, will remove
          // Put llm first
          if (a.reviewer === "llm") return -1;
          if (b.reviewer === "llm") return 1;
          // Then austin
          if (a.reviewer === "austin") return -1;
          if (b.reviewer === "austin") return 1;
          // Then human
          if (a.reviewer === "human") return -1;
          if (b.reviewer === "human") return 1;
          // Keep original order for other entries
          return 0;
        })
        .map((entry, i) => {
          const field = entry.fields.filter((field) => field.fieldName === fieldName)[0];
          const answerArray = Array.from(field.answer).sort();
          const additionalInputs = field.additionalInputs;

          // Calculate the source number for non-llm and non-austin entries
          const sourceNumber =
            reviewHistory
              .slice(0, i)
              .filter(
                (item) =>
                  item.reviewer !== "llm" &&
                  item.reviewer !== "austin" &&
                  item.reviewer !== "human",
              ).length + 1;

          return (
            <div key={i} className="review-row">
              {isAdminUser(ldap) && entry.reviewer === "llm" ? (
                <div className="reviewer-name">{`LLM:`}</div>
              ) : isAdminUser(ldap) && entry.reviewer === "austin" ? (
                <div className="reviewer-name">{`AUSTIN:`}</div>
              ) : isAdminUser(ldap) && entry.reviewer === "human" ? (
                <div className="reviewer-name">{`Human:`}</div>
              ) : (
                <div className="reviewer-name">{`Source ${sourceNumber}:`}</div>
              )}

              <div className="answers-container">
                {answerArray.map((answerItem, answerIndex) => {
                  const colorInfo = answerColorMap[answerItem];
                  return (
                    <div
                      key={answerIndex}
                      className="answer-box"
                      style={{
                        backgroundColor: colorInfo.bg,
                        color: colorInfo.text,
                      }}
                    >
                      {answerItem.split(HIERARCHY_SEPARATOR).slice(-1)[0]}
                    </div>
                  );
                })}
                {showAdditionalInputs &&
                  additionalInputs &&
                  Object.entries(additionalInputs).map(([key, value]) => (
                    <div key={key} style={{ width: "100%" }}>
                      <div
                        className="answer-box"
                        style={{ backgroundColor: "#f0f0f0", color: "#333" }}
                      >
                        {`${key}: ${value}`}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default ReviewHistory;
